import React, { useState, useEffect } from 'react';
import InfoBox from './InfoBox';
import { useAuth } from '../context/AuthContext';

const Tour: React.FC = () => {
  const { userType } = useAuth();
  const [step, setStep] = useState(0);
  const [element, setElement] = useState<Element | null>(null);

  const handleClose = () => {
    setStep(step + 1);
  };

  const allTourSteps = [
    {
      text: 'Welcome to your dashboard! Here you can get a quick overview of your job search.',
      selector: '[data-tour-id="welcome-message"]',
      userType: 'jobseeker',
    },
    {
      text: 'These cards show you a summary of your applications.',
      selector: '[data-tour-id="stats-cards"]',
      userType: 'jobseeker',
    },
    {
      text: 'Here you can see your most recent applications and their status.',
      selector: '[data-tour-id="recent-applications"]',
      userType: 'jobseeker',
    },
    {
      text: 'Use this button to find and apply to more jobs.',
      selector: '[data-tour-id="apply-to-more-jobs-button"]',
      userType: 'jobseeker',
    },
    {
      text: 'You can chat with companies you have applied to here.',
      selector: '[data-tour-id="chat-section"]',
      userType: 'jobseeker',
    },
    {
      text: 'Welcome to your company dashboard! Here you can manage your job postings and candidates.',
      selector: '[data-tour-id="welcome-message"]',
      userType: 'company',
    },
    {
      text: 'These cards show you a summary of your job postings and applications.',
      selector: '[data-tour-id="stats-cards"]',
      userType: 'company',
    },
    {
      text: 'Here you can see your most recent job postings and their status.',
      selector: '[data-tour-id="job-postings"]',
      userType: 'company',
    },
    {
      text: 'Use this button to create a new job posting.',
      selector: '[data-tour-id="create-new-job-posting-button"]',
      userType: 'company',
    },
    {
      text: 'Find potential candidates for your job openings here.',
      selector: '[data-tour-id="potential-candidates"]',
      userType: 'company',
    },
    {
      text: 'You can chat with job seekers who have applied to your jobs here.',
      selector: '[data-tour-id="chat-section"]',
      userType: 'company',
    },
    {
      text: 'This is your profile page. You can edit your personal and professional information here.',
      selector: '[data-tour-id="profile-heading"]',
      userType: 'jobseeker',
    },
    {
      text: 'Click here to edit your profile information.',
      selector: '[data-tour-id="edit-profile-button"]',
      userType: 'jobseeker',
    },
    {
      text: 'This section shows your profile completeness and suggestions to improve it.',
      selector: '[data-tour-id="profile-progress"]',
      userType: 'jobseeker',
    },
    {
      text: 'This is your company profile page. You can edit your company information here.',
      selector: '[data-tour-id="profile-heading"]',
      userType: 'company',
    },
    {
      text: 'Click here to edit your company profile information.',
      selector: '[data-tour-id="edit-profile-button"]',
      userType: 'company',
    },
    {
      text: 'This section shows your profile completeness and suggestions to improve it.',
      selector: '[data-tour-id="profile-progress"]',
      userType: 'company',
    },
    {
      text: 'Use these filters to search for jobs by title, company, or location.',
      selector: '[data-tour-id="search-filters"]',
      userType: 'jobseeker',
    },
    {
      text: 'Browse through the available job listings here.',
      selector: '[data-tour-id="job-listings"]',
      userType: 'jobseeker',
    },
  ];

  const tourSteps = allTourSteps.filter(step => step.userType === userType);

  useEffect(() => {
    if (step >= tourSteps.length) {
      setElement(null);
      return;
    }

    const currentStep = tourSteps[step];
    let attempts = 0;
    const maxAttempts = 50; // Poll for 5 seconds (50 * 100ms)

    const interval = setInterval(() => {
      const el = document.querySelector(currentStep.selector);
      if (el) {
        setElement(el);
        clearInterval(interval);
      } else {
        attempts++;
        if (attempts >= maxAttempts) {
          console.error(`Tour failed: Could not find element with selector ${currentStep.selector}`);
          clearInterval(interval);
          setElement(null); // Hide tour if element not found
        }
      }
    }, 100);

    return () => clearInterval(interval);
  }, [step, tourSteps]);

  if (step >= tourSteps.length || !element) {
    return null;
  }

  const rect = element.getBoundingClientRect();

  return (
    <div style={{ position: 'fixed', top: rect.bottom + 10, left: rect.left, zIndex: 1000 }}>
      <InfoBox text={tourSteps[step].text} onClose={handleClose} />
    </div>
  );
};

export default Tour;
